import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useDeletePricingPlanTier } from 'src/features/pricingPlan/api/tiers/useDeletePricingPlanTier';
import { LIST_PRICING_PLAN_TIERS_RESOURCE } from 'src/features/pricingPlan/api/tiers/useListPricingPlanTiers';
import { PricingPlanTier } from 'src/features/pricingPlan/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';

interface PricingPlanTierFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedTier: PricingPlanTier;
    children?: React.ReactNode;
}

function DeletePricingPlanTierFormModal<TData>({ selectedTier, children }: PricingPlanTierFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { planId = '' } = useParams();
    const { parkingId } = useParking();

    const { mutate: deleteTier } = useDeletePricingPlanTier({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PRICING_PLAN_TIERS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(pricingPlanMessages.deleteTierSuccess),
                description: formatMessage(pricingPlanMessages.deleteTierSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...generalMessages.delete },
                subTitle: { ...pricingPlanMessages.deleteTierSubtitle },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...pricingPlanMessages.confirmDeleteTier },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: generalMessages.delete,
                callbackFn: () => {
                    return (deleteTier({ planId, tierId: selectedTier.id, parkingId }), setIsModalOpen(false));
                },
            }}
            children={children}
        />
    );
}

export default DeletePricingPlanTierFormModal;
